'use client';

import { Box, Button, Flex, FormLabel, Input, Typography } from '@coolblue-development/freddy';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from '@i18n/useTranslation';
import { useActionState, useEffect, useId, useTransition } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';

import { FormStateProps, subscribeToNewsletter } from './actions';

const initialState: FormStateProps = {
  success: false,
  error: false
};

type FreeWashingSubscriptionFormProps = {
  onSuccessAction?: () => void;
};

export default function FreeWashingSubscriptionForm({
  onSuccessAction
}: FreeWashingSubscriptionFormProps) {
  const { t } = useTranslation('webshop.productPage.freeWashing');

  const [state, formAction, formPending] = useActionState(subscribeToNewsletter, initialState);
  const [transitionPending, startTransition] = useTransition();

  const baseId = useId();
  const ids = {
    helper: `${baseId}-helper`,
    error: `${baseId}-error`,
    email: `${baseId}-email`
  };

  const formSchema = z.object({
    email: z.string().min(1, t('emailEmpty')).email(t('emailInvalid'))
  });

  type FormFields = z.infer<typeof formSchema>;

  const {
    control,
    handleSubmit,
    getValues,
    formState: { errors }
  } = useForm<FormFields>({
    mode: 'onSubmit',
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: '',
      ...(state?.fields ?? {})
    }
  });

  const handleFormSubmit = () => {
    const formData = new FormData();
    formData.append('email', getValues('email'));

    startTransition(async () => {
      formAction(formData);
    });
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSubmit(handleFormSubmit)();
    }
  };

  useEffect(() => {
    if (state?.success && onSuccessAction) {
      onSuccessAction();
    }
  }, [state?.success, onSuccessAction]);

  const hasError = !!errors.email || !!state?.error;
  const isSuccess = !!state?.success;

  const ariaDescribedBy = hasError ? undefined : ids.helper;

  return (
    <Box>
      <FormLabel htmlFor={ids.email}>{t('emailFieldLabel')}</FormLabel>

      <Flex gap={200} alignItems='flex-end' mt={100}>
        <Box flex={1}>
          <Controller
            control={control}
            name='email'
            render={({ field, fieldState }) => (
              <Input
                id={ids.email}
                type='email'
                validation={fieldState?.error ? 'error' : undefined}
                aria-describedby={ariaDescribedBy}
                aria-invalid={hasError || undefined}
                aria-required='true'
                autoComplete='email'
                inputMode='email'
                onKeyDown={handleKeyDown}
                {...field}
              />
            )}
          />
        </Box>

        <Button
          type='button'
          variant='primary'
          size='small'
          loading={formPending || transitionPending}
          disabled={formPending || transitionPending || isSuccess}
          onClick={() => {
            handleSubmit(handleFormSubmit)();
          }}
        >
          {isSuccess ? t('subscribeButton') + ' ✓' : t('subscribeButton')}
        </Button>
      </Flex>

      {!hasError && (
        <Box id={ids.helper}>
          <Typography mt={100} color='subtle'>
            {t('emailFieldHelperText')}
          </Typography>
        </Box>
      )}

      {hasError && (
        <Box id={ids.error} role='alert'>
          {state?.error && (
            <Typography mt={100} color='sentimentDanger'>
              {t('subscriptionFailedMessage')}
            </Typography>
          )}
          {errors.email && (
            <Typography mt={100} color='sentimentDanger'>
              {errors.email.message}
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
}
